import { errorHandlerReport } from "@mgdx-libs/error-handler";
import { logger } from "@mgdx-libs/logger";
import type { GatsbyBrowser } from "gatsby";
import * as ReactDOM from "react-dom/client";

const reportErrorSafely = (error: string | Error) => {
  void errorHandlerReport(error).catch((reportError) => {
    logger.error(reportError);
  });
};

const stringifyUnknownReason = (reason: unknown): string => {
  if (typeof reason === "string") return reason;
  if (reason === null) return "null";
  if (reason === undefined) return "undefined";

  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

const createErrorFromErrorEvent = (event: ErrorEvent): Error => {
  if (event.error instanceof Error) {
    return event.error;
  }

  const hasFilename = typeof event.filename === "string" && event.filename !== "";
  const hasLineno = typeof event.lineno === "number";
  const hasColno = typeof event.colno === "number";
  const location = hasFilename && hasLineno && hasColno ? ` (${event.filename}:${event.lineno}:${event.colno})` : "";

  return new Error(`${event.message}${location}`);
};

const createErrorFromUnhandledRejection = (event: PromiseRejectionEvent): Error => {
  if (event.reason instanceof Error) {
    return event.reason;
  }

  return new Error(`Unhandled promise rejection. ${stringifyUnknownReason(event.reason)}`);
};

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  window.global = window;

  window.addEventListener("error", (event: unknown) => {
    if (event instanceof ErrorEvent) {
      reportErrorSafely(createErrorFromErrorEvent(event));
    } else if (event instanceof Event || event instanceof UIEvent) {
      reportErrorSafely(`window.error: ${event.type} ${event.target}`);
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (event) {
      reportErrorSafely(createErrorFromUnhandledRejection(event));
    }
  });

  if (process.env.ENABLE_DEBUG === "true") {
    localStorage.debug = "app:*";
  } else {
    localStorage.debug = "";
  }
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = (args, _options) => {
  const { routerProps, prevRouterProps } = args;

  if (routerProps && prevRouterProps) {
    if (routerProps.location.pathname === prevRouterProps.location.pathname) {
      logger.debug("Same location. Do not update scroll position. %o", args);
      return false;
    }
  }

  return true;
};

// @see https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#replaceHydrateFunction
// Replace the ReactDOM.createRoot / render function.
export const replaceHydrateFunction: GatsbyBrowser["replaceHydrateFunction"] =
  (_args, _options) => (element, container, _callback) => {
    const root = ReactDOM.createRoot(container);
    root.render(element);
    return element;
  };
